import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Button from '../UI/Button';
import { countryService } from '../../services/countryService';
import { Country, CreateCountryRequest, UpdateCountryRequest } from '../../types/client';

interface Props {
  country?: Country | null;
  onClose: () => void;
  onSuccess: () => void;
}

const CountryModal: React.FC<Props> = ({ country, onClose, onSuccess }) => {
  const [error, setError] = useState<string | null>(null);
  const isEditing = !!country;

  const { register, handleSubmit, formState: { errors }, reset } = useForm<CreateCountryRequest>({
    defaultValues: {
      name: country?.name || '',
      code: country?.code || '',
      status: country?.status || 'ACTIVE',
    }
  });

  useEffect(() => {
    if (country) {
      reset({ name: country.name, code: country.code, status: country.status });
    }
  }, [country, reset]);

  const createMutation = useMutation({
    mutationFn: (data: CreateCountryRequest) => countryService.createCountry(data),
    onSuccess,
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to create country'),
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateCountryRequest) => countryService.updateCountry(country!.id, data),
    onSuccess,
    onError: (e: any) => setError(e?.response?.data?.error || 'Failed to update country'),
  });

  const onSubmit = (data: CreateCountryRequest) => {
    setError(null);
    isEditing ? updateMutation.mutate(data) : createMutation.mutate(data);
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Transition appear show={true} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child as={React.Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">{isEditing ? 'Edit Country' : 'Create Country'}</Dialog.Title>
                  <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="h-6 w-6" /></button>
                </div>
                {error && (<div className="mb-4 rounded-lg bg-red-50 p-4"><div className="text-sm text-red-700">{error}</div></div>)}
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div className="grid grid-cols-1 gap-4">
                    <div>
                      <label className="form-label">Name *</label>
                      <input {...register('name', { required: 'Name is required', maxLength: { value: 100, message: 'Max 100 chars' } })} type="text" className="form-input" placeholder="Enter country name" />
                      {errors.name && <p className="form-error">{errors.name.message}</p>}
                    </div>
                    <div>
                      <label className="form-label">Code *</label>
                      <input {...register('code', { required: 'Code is required', maxLength: { value: 10, message: 'Max 10 chars' } })} type="text" className="form-input" placeholder="e.g. LB, US" />
                      {errors.code && <p className="form-error">{errors.code.message}</p>}
                    </div>
                    <div>
                      <label className="form-label">Status</label>
                      <select {...register('status')} className="form-input">
                        <option value="ACTIVE">Active</option>
                        <option value="INACTIVE">Inactive</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-3 pt-4">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
                    <Button type="submit" loading={isLoading}>{isEditing ? 'Update' : 'Create'}</Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default CountryModal;

